import { LocalSpaceOffsetCalculator } from './LocalSpaceOffsetCalculator';
import { WorldSpaceOffsetCalculator } from './WorldSpaceOffsetCalculator';
import { ScaleInfo } from './ScaleInfo';

declare const THREE: any;

interface ISize {
  length: number;
  height: number;
}

export interface IConfig {
  object: any;
  newSize: ISize;
  currentSize: ISize;
  includeChildren: boolean;
  scaleInfo: ScaleInfo;
}

interface IChangeOriginConfig {
  object: any;
  scaleInfo: ScaleInfo;
  size: ISize;
  meshesToTransform: any[];
  translateTransform: (translate: any) => any;
  offsetTransform: (offfset: any) => any;
}

export class ObjectResizer {
  private static _transforms = {
    noTransform: input => input,
    negateTransform: input => input.negate()
  };

  private _localSpaceOffsetCalculator: LocalSpaceOffsetCalculator = new LocalSpaceOffsetCalculator();
  private _worldSpaceOffsetCalculator: WorldSpaceOffsetCalculator = new WorldSpaceOffsetCalculator();
  private _scaleMatrix = new THREE.Matrix4();

  private static _shiftObject(object, offset) {
    object.getWorldPosition(object.position);
    object.position.add(offset);
    object.parent.worldToLocal(object.position);
  }

  private static _isMesh = object => object instanceof THREE.Mesh;

  private _changeOrigin(config: IChangeOriginConfig) {
    const {
      object,
      scaleInfo,
      size: { length, height },
      meshesToTransform,
      offsetTransform,
      translateTransform
    } = config;
    const translate = translateTransform(
      this._localSpaceOffsetCalculator.calculate(scaleInfo.localScaleDirection, length, height)
    );
    meshesToTransform.forEach(mesh => {
      mesh.geometry.translate(translate.x, translate.y, translate.z);
    });
    const offset = offsetTransform(
      this._worldSpaceOffsetCalculator.calculate(scaleInfo, length / 2, height / 2)
    );
    ObjectResizer._shiftObject(object, offset);
  }

  private _transformOrigin(config: IConfig, meshesToTransform) {
    const { object, scaleInfo, currentSize } = config;
    this._changeOrigin({
      object,
      scaleInfo,
      meshesToTransform,
      size: currentSize,
      translateTransform: ObjectResizer._transforms.noTransform,
      offsetTransform: ObjectResizer._transforms.negateTransform
    });
  }

  private _performScale(config: IConfig, meshesToTransform) {
    const { newSize, currentSize } = config;
    const xScale = newSize.length / currentSize.length;
    const yScale = newSize.height / currentSize.height;

    this._scaleMatrix.identity().makeScale(xScale, yScale, 1);

    meshesToTransform.forEach(mesh => {
      mesh.geometry.applyMatrix(this._scaleMatrix);
    });
  }

  private _restoreOrigin(config: IConfig, meshesToTransform) {
    const { scaleInfo, newSize, object } = config;
    this._changeOrigin({
      object,
      scaleInfo,
      meshesToTransform,
      size: newSize,
      translateTransform: ObjectResizer._transforms.negateTransform,
      offsetTransform: ObjectResizer._transforms.noTransform
    });
  }

  resize(config: IConfig) {
    const { object, includeChildren } = config;
    let meshesToTransform = ObjectResizer._isMesh(object) ? [object] : [];
    if (includeChildren) {
      meshesToTransform = meshesToTransform.concat(object.children.filter(ObjectResizer._isMesh));
    }
    this._transformOrigin(config, meshesToTransform);
    this._performScale(config, meshesToTransform);
    this._restoreOrigin(config, meshesToTransform);
  }
}



// WEBPACK FOOTER //
// ./src/app/Component(2D and 3D)/Shared/ObjectResize/ObjectResizer.ts